import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, Code2, Maximize2, X } from "lucide-react";
import { AreaTrendChart, BarChart, CAT, RankedBars, StatTile, type Row, type SeriesDef } from "@/components/charts";
import type { TalkToDataDashboard } from "@/components/ChatbotBubble";
import { cn } from "@/lib/utils";

/** "first_name" -> "First Name" — the table/chart shows the real result rows
 * (the admin asked to see the actual records, not just a prose paraphrase of
 * them), but a raw snake_case DB column as a header still reads like
 * internal plumbing leaking into the UI. Only the DISPLAYED label changes;
 * lookups into a row still use the real column key. */
function humanizeColumn(col: string): string {
  return col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isNumeric(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/** Columns where every non-null value across the sample is a number — the
 * measures a chart can actually plot, as opposed to the label/category. */
function numericColumns(columns: string[], data: Record<string, unknown>[]): string[] {
  return columns.filter((c) => data.some((r) => isNumeric(r[c])));
}

/** SQL date/timestamp columns arrive as full ISO strings
 * ("2026-06-29T00:00:00+00:00") — by far the messiest label an axis can get:
 * 25 characters where "Jun 29" would do, and the reason a 4-point weekly
 * trend still looked crowded. Detected once per chart from a real sample
 * value rather than the column name, since a generated query can name a
 * date column almost anything. */
function isDateLike(value: unknown): boolean {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value) && !Number.isNaN(Date.parse(value));
}

/** A "2026-09-24" date parsed as a LOCAL calendar date. `new Date("2026-09-24")`
 * is UTC midnight, which toLocaleDateString then renders as Sep 23 anywhere
 * west of UTC — a date-only value must stay the day it says. Anything else
 * (an older stored timestamp) falls back to the normal parser. */
function parseDate(v: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
}

function formatAxisDate(v: string): string {
  const d = parseDate(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTooltipDate(v: string): string {
  const d = parseDate(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** Columns that hold money, by name. The result is raw rows from an arbitrary
 * generated query, so there is no schema to ask — a name is all there is. */
const MONEY_COLUMN = /(^|_)(revenue|ebitda|price|amount|cost|profit|income|mrr|arr|budget)(_|$)/i;

function isMoneyColumn(col: string): boolean {
  return MONEY_COLUMN.test(col);
}

/** $1.25M — dollars, always in millions. Amounts under $10,000 stay as plain
 * dollars ($79): in millions they would round to $0.01M or $0.00M and say
 * nothing, and forcing that would make the number wrong, not tidier. */
function formatMillions(n: number): string {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (abs < 10_000) return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
}

/** A stored timestamp ("2026-09-24T07:33:20+00:00", from history saved before
 * results were made date-only) shown as just its date. */
const TIMESTAMP = /^(\d{4}-\d{2}-\d{2})[T ]\d{2}:\d{2}/;

function formatCell(col: string, value: unknown): string {
  if (value === null || value === undefined) return "";
  if (isMoneyColumn(col) && isNumeric(value)) return formatMillions(value);
  if (typeof value === "string") {
    const m = TIMESTAMP.exec(value);
    if (m) return m[1];
  }
  return String(value);
}

const UUID_VALUE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Primary/foreign key columns never show — by name (id, *_id) or because every
 * value is a UUID. The server strips these too; this covers history saved
 * before it did. A result made only of keys is left alone rather than emptied. */
function withoutIdentifiers(dashboard: TalkToDataDashboard): TalkToDataDashboard {
  const isKey = (col: string) =>
    /^(id|uuid)$|_id$|_uuid$/i.test(col) ||
    (() => {
      const vals = dashboard.data.map((r) => r[col]).filter((v) => v !== null && v !== undefined);
      return vals.length > 0 && vals.every((v) => typeof v === "string" && UUID_VALUE.test(v));
    })();
  const columns = dashboard.columns.filter((c) => !isKey(c));
  return columns.length === 0 || columns.length === dashboard.columns.length ? dashboard : { ...dashboard, columns };
}

/**
 * The real charted view for a dashboard result, built from this app's own
 * existing, already theme-correct, already colorblind-validated chart
 * primitives (see components/charts.tsx) — not a new charting library.
 *
 * A plain function, deliberately NOT a component: it returns `ReactNode |
 * null` directly, so the caller can tell "no chart fits this shape" apart
 * from "rendered nothing yet". `const el = <DashboardChart .../>; if
 * (!el)` looks like it does that but doesn't — `el` is a JSX element
 * descriptor, always truthy, regardless of what the component returns once
 * React actually renders it. That bug shipped and was confirmed live: a
 * result with no numeric column (e.g. first_name/last_name/email/
 * created_at — a plain "list the users" query) legitimately has no chart to
 * draw, this function correctly falls through to its final `return null`,
 * and the old truthy-JSX check rendered that "chart" anyway — an empty box,
 * with the actual records table it should have fallen back to never shown.
 */
function renderDashboardChart(
  dashboard: TalkToDataDashboard,
  height: number,
  /** Smaller in the compact inline card than the default charts.tsx uses
   * everywhere else (analytics dashboards, full page width) — see
   * charts.tsx's own labelFontSize prop docs. Unset (undefined) in the
   * maximized modal, which falls back to that same default since there's
   * no crowding to solve there. */
  labelFontSize?: number,
): ReactNode | null {
  const { chart_type, columns, data } = dashboard;
  if (data.length === 0) return null;

  const numCols = numericColumns(columns, data);
  const labelCol = columns.find((c) => !numCols.includes(c));

  if (chart_type === "metric_card" && data.length === 1 && numCols.length >= 1) {
    const col = numCols[0];
    const value = data[0][col];
    return (
      <StatTile
        label={humanizeColumn(col)}
        value={isNumeric(value) ? (isMoneyColumn(col) ? formatMillions(value) : value.toLocaleString()) : formatCell(col, value)}
      />
    );
  }

  // A single measure ranked by a label — "top N by X" is the shape almost
  // every "dashboard" question in this feature actually asks for, and reads
  // far better as a horizontal ranked bar than a full axis'd bar chart in a
  // narrow chat panel.
  if (chart_type === "bar" && labelCol && numCols.length === 1) {
    const valueCol = numCols[0];
    const rows = data
      .map((r) => ({ label: String(r[labelCol] ?? ""), value: isNumeric(r[valueCol]) ? r[valueCol] : 0 }))
      .slice(0, 20);
    // Ranked labels are often full listing titles, easily 60+ characters —
    // wrapping to two lines reads far better than a mid-word ellipsis.
    return (
      <RankedBars
        rows={rows}
        formatValue={(n) => (isMoneyColumn(valueCol) ? formatMillions(n) : n.toLocaleString())}
        wrapLabels
      />
    );
  }

  if (labelCol && numCols.length >= 1 && (chart_type === "bar" || chart_type === "line" || chart_type === "area")) {
    // Categorical hues assigned in FIXED order, never cycled — same rule
    // charts.tsx's own components already follow.
    const series: SeriesDef[] = numCols
      .slice(0, 2)
      .map((c, i) => ({ key: c, label: humanizeColumn(c), color: CAT[i % CAT.length] }));
    const rows: Row[] = data.map((r) => r as Row);
    const dateAxis = isDateLike(data[0]?.[labelCol]);
    const dateProps = dateAxis ? { formatX: formatAxisDate, formatTooltipX: formatTooltipDate } : {};
    // Money series read as $1.25M on the axis and in the tooltip; anything else
    // (counts, mixed series) keeps the default numbers.
    const moneyProps = series.every((s) => isMoneyColumn(s.key))
      ? { formatValue: formatMillions, formatTooltipValue: formatMillions }
      : {};
    const axisProps = { ...dateProps, ...moneyProps };
    return chart_type === "bar" ? (
      <BarChart data={rows} xKey={labelCol} series={series} height={height} labelFontSize={labelFontSize} {...axisProps} />
    ) : (
      <AreaTrendChart data={rows} xKey={labelCol} series={series} height={height} labelFontSize={labelFontSize} {...axisProps} />
    );
  }

  return null;
}

function RecordsTable({ dashboard, maxHeightClass }: { dashboard: TalkToDataDashboard; maxHeightClass: string }) {
  return (
    <div className={cn("overflow-auto", maxHeightClass)}>
      <table className="w-full text-left text-[11px]">
        <thead className="sticky top-0 bg-card">
          <tr>
            {dashboard.columns.map((col) => (
              <th key={col} className="whitespace-nowrap border-b border-border/60 px-2.5 py-1.5 font-semibold text-muted-foreground">
                {humanizeColumn(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dashboard.data.map((row, i) => (
            <tr key={i} className="border-b border-border/40 last:border-0">
              {dashboard.columns.map((col) => (
                <td key={col} className="whitespace-nowrap px-2.5 py-1.5 text-foreground/90">
                  {formatCell(col, row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Chart-or-table body, shared by the compact inline card and the maximized
 * modal so the two views can never drift apart on what they show — only how
 * much room they're given to show it in.
 *
 * `compact` wraps the chart in a horizontally-scrollable min-width strip:
 * BarChart/AreaTrendChart measure and fill whatever container width they're
 * given, which in Niva's narrow docked drawer meant every axis/label
 * shrank down to an unreadable size instead of ever getting a scrollbar —
 * a chart should get smaller by scrolling, not by every label overlapping.
 */
function DashboardBody({
  dashboard,
  compact,
}: {
  dashboard: TalkToDataDashboard;
  compact: boolean;
}) {
  const chart = renderDashboardChart(dashboard, compact ? 200 : 420, compact ? 11 : undefined);
  if (chart === null) {
    return <RecordsTable dashboard={dashboard} maxHeightClass={compact ? "max-h-64" : "max-h-[70vh]"} />;
  }
  if (!compact) return chart;
  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <div style={{ minWidth: 420 }}>{chart}</div>
    </div>
  );
}

/** Pop-out for a chart/table that's too compact to read inline — a centered
 * dialog in the MAIN page area, the same hand-rolled modal treatment used
 * everywhere else in this app (see PublishConfirmationModal.tsx: a
 * translucent backdrop behind a single centered card), not the edge-to-edge
 * dark theater mode ImageLightbox.tsx uses for photos.
 *
 * Portalled to document.body — same reason as every other modal in this
 * codebase that does this (UnlockListingModal.tsx etc.): Niva's drawer slides
 * in via a CSS `transform` (.niva-drawer-in in index.css), and a `transform`
 * on any ancestor creates a new containing block for `position: fixed`
 * descendants. Left un-portalled, this modal was positioned/clipped relative
 * to the drawer's own box instead of the real viewport — confirmed live,
 * this is exactly why it looked trapped inside the Niva frame instead of
 * showing in the main page. z-[100] matches this codebase's convention for a
 * body-portalled modal (clears the top nav at z-50 and the sidebar toggle at
 * z-60), and Niva's own z-51..53 no longer matter once this is outside its
 * subtree. */
function DashboardModal({ dashboard, onClose }: { dashboard: TalkToDataDashboard; onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={dashboard.title || "Query result"}
    >
      <div className="flex max-h-[90dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-card shadow-2xl">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-4">
          <p className="truncate text-base font-extrabold text-foreground">{dashboard.title || "Query result"}</p>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Close"
            title="Close (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-5">
          <DashboardBody dashboard={dashboard} compact={false} />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Renders one Talk-to-Your-Data turn's extra payload: a real chart when the
 * result shape supports one (see renderDashboardChart), the plain records table
 * otherwise, and the generated SQL (collapsed by default — useful for the
 * admin to trust/debug, irrelevant noise otherwise) always last.
 */
export function TalkToDataResult({
  sql,
  dashboard: rawDashboard,
}: {
  sql: string | null;
  dashboard: TalkToDataDashboard | null;
}) {
  const [sqlOpen, setSqlOpen] = useState(false);
  const [maximized, setMaximized] = useState(false);
  // Key columns are removed once, here, so the inline card, the maximized
  // dialog, the chart and the table can never disagree about what's shown.
  const dashboard = rawDashboard ? withoutIdentifiers(rawDashboard) : null;

  return (
    <div className="space-y-2">
      {dashboard && dashboard.data.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-border/70 bg-card/80 p-3">
          <div className="mb-2 flex items-start justify-between gap-2">
            {dashboard.title ? (
              <p className="text-[11px] font-semibold text-foreground">{dashboard.title}</p>
            ) : (
              <span />
            )}
            {/* Niva's own docked drawer is narrow enough that a chart's axis
                labels can overlap before they'd ever need to — this is the
                explicit way out, independent of (and in addition to) the
                drawer's own widen button in the header above. */}
            <button
              type="button"
              onClick={() => setMaximized(true)}
              className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              aria-label="View full size"
              title="View full size"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <DashboardBody dashboard={dashboard} compact />
        </div>
      )}
      {maximized && dashboard && <DashboardModal dashboard={dashboard} onClose={() => setMaximized(false)} />}
      {/* Always last: the reply text and any chart/table above it are the
          actual answer, this is just how-we-got-there for trust/debugging. */}
      {sql && (
        <div className="rounded-xl border border-border/70 bg-card/80">
          <button
            type="button"
            onClick={() => setSqlOpen((v) => !v)}
            className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            {sqlOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <Code2 className="h-3.5 w-3.5" />
            View SQL
          </button>
          {sqlOpen && (
            <pre className="overflow-x-auto whitespace-pre-wrap break-words border-t border-border/60 px-3 py-2 text-[11px] text-foreground/80">
              {sql}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
